import React, { useEffect, useRef, useState } from 'react';

const tenets = [
  {
    tag: 'GROUND TRUTH',
    sub: 'Beyond consensus.',
    text: 'We believe conviction can never be borrowed from a pitch deck. In high-stakes strategy, the real answers live beneath the consensus. We look for ground truth with quiet discipline, testing assumptions until only the facts remain.',
  },
  {
    tag: 'HUMAN JUDGMENT',
    sub: 'Elevated, not replaced.',
    text: 'Technology should never displace human responsibility. We build machines that absorb the brute-force complexity of transactions, freeing dealmakers to focus on what matters most: strategic intuition, sharp judgment, and decisive action.',
  },
  {
    tag: 'FIDUCIARY CARE',
    sub: 'Respect for the stakes.',
    text: 'We build for moments where failure is not an option. The capital we touch shapes industries, economies, and futures. We treat every mandate with deep institutional humility, absolute discretion, and an uncompromising standard of craft.',
  },
];

// Section 2 — The Frontier Way: sticky title + scroll-illuminated doctrine tenets.
export const FrontierWaySection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    const update = () => {
      const section = sectionRef.current;
      const items = itemRefs.current.filter(Boolean);
      if (!section || items.length === 0) return;

      const focusY = window.innerHeight * 0.45;
      let active = 0;
      let closest = Infinity;
      items.forEach((item, i) => {
        const rect = item.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const dist = Math.abs(center - focusY);
        if (dist < closest) { closest = dist; active = i; }
      });

      const sr = section.getBoundingClientRect();
      const inZone = sr.top < window.innerHeight * 0.75 && sr.bottom > window.innerHeight * 0.25;
      if (inZone) setActiveIndex(active);
      else if (sr.top >= window.innerHeight * 0.75) setActiveIndex(0);
      else if (sr.bottom <= window.innerHeight * 0.25) setActiveIndex(items.length - 1);
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-24 sm:py-36 px-6 sm:px-10 lg:px-16 border-b border-stone-200/80 bg-[#FBFBFB]">
      <div id="frontier-way" className="scroll-mt-20 w-full max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
        <div className="lg:col-span-4">
          <h2 className="text-3xl sm:text-5xl font-medium tracking-tight text-stone-950 sticky top-28">The Frontier way</h2>
        </div>
        <div className="lg:col-span-8 space-y-24 sm:space-y-32 max-w-3xl">
          {tenets.map((t, i) => (
            <div
              key={t.tag}
              ref={(el) => { itemRefs.current[i] = el; }}
              className={`tenet-item cursor-default ${activeIndex === i ? 'active' : ''}`}
            >
              <div className="text-xs uppercase tracking-wider mb-4 flex items-center gap-2 font-sans">
                <span className="font-bold tenet-tag-bold tracking-wider">{t.tag}</span>
                <span className="tenet-tag-sub font-normal">{t.sub}</span>
              </div>
              <p className="tenet-text text-2xl sm:text-3xl font-medium tracking-tight leading-[1.35] max-w-2xl font-sans">
                {t.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
